import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { baseURL } from '../services/api';

const resolveSocketBase = () => {
  const explicitWsBase = (process.env.REACT_APP_WS_BASE_URL || '').trim();
  if (explicitWsBase) {
    return explicitWsBase;
  }

  try {
    if (baseURL) {
      return new URL(baseURL, window.location.origin).origin;
    }
  } catch {
    // fallback below
  }

  return window.location.origin;
};

const buildSocketUrl = (stageKey, projectId, token) => {
  const socketBase = resolveSocketBase();
  const url = new URL(`/ws/repairs/stage/${stageKey}/`, socketBase);
  url.protocol = url.protocol.replace('http', 'ws');
  url.searchParams.set('project_id', projectId);
  if (token) {
    url.searchParams.set('token', token);
  }
  return url.toString();
};

/**
 * Подключает страницу к веб-сокету этапа оборудования и реагирует на события.
 */
const useEquipmentRealtime = (stageKey, onMessage) => {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const openedRef = useRef(false);
  const projectId = typeof window !== 'undefined' ? localStorage.getItem('selected_project_id') : null;
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  const reconnectTimer = useRef(null);

  useEffect(() => {
    if (!user || !token || !projectId || !stageKey) return () => {};

    let cancelled = false;
    let reconnectAttempt = 0;

    // Небольшая задержка сглаживает двойной mount React.StrictMode (dev)
    const start = () => {
      if (cancelled) return;
      const currentToken = localStorage.getItem('access_token');
      if (!currentToken) return;
      const url = buildSocketUrl(stageKey, projectId, currentToken);
      const ws = new WebSocket(url);
      socketRef.current = ws;
      openedRef.current = false;

      ws.onopen = () => {
        reconnectAttempt = 0;
        openedRef.current = true;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug(`useEquipmentRealtime(${stageKey}): open`, { url });
        }
        // Отправим тестовый ping (consumer вернёт {type: 'ok'})
        try { ws.send(JSON.stringify({ action: 'ping' })); } catch (_) {}
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          // Игнор служебного ответа
          if (payload && payload.type === 'ok' && !payload.action) return;
          if (payload && payload.action && typeof onMessage === 'function') {
            onMessage(payload);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('useEquipmentRealtime: parse error', err);
        }
      };

      ws.onerror = (event) => {
        // Часто в dev первый сокет закрывается из-за StrictMode (mount/unmount) → не шумим, если не было onopen
        if (!openedRef.current) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug(`useEquipmentRealtime(${stageKey}): early error (likely dev remount)`, event);
          }
          return;
        }
        // eslint-disable-next-line no-console
        console.error(`useEquipmentRealtime(${stageKey}): socket error`, event);
      };

      ws.onclose = (evt) => {
        const code = evt?.code;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
            console.debug(`useEquipmentRealtime(${stageKey}): close`, { code, reason: evt?.reason });
        }
        // Авто-реконнект (кроме явного демонтирования / закрытия страницы)
        if (!cancelled && code !== 1000) {
          if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
          reconnectAttempt += 1;
          reconnectTimer.current = setTimeout(() => {
            start();
          }, Math.min(30000, 1000 * (2 ** reconnectAttempt)));
        }
      };
    };

    // Старт с микро‑задержкой
    const delay = setTimeout(start, 40);

    return () => {
      cancelled = true;
      if (delay) clearTimeout(delay);
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        try { socketRef.current.close(1000, 'component unmount'); } catch (_) {}
      } else if (socketRef.current) {
        try { socketRef.current.close(); } catch (_) {}
      }
      socketRef.current = null;
    };
  }, [stageKey, user, onMessage, projectId, token]);
};

export default useEquipmentRealtime;
